'use client';

import { bytesToHex } from '@noble/hashes/utils.js';

export { hashBrowserUploadBody } from './browser-upload-hash';

const CONTROL_PREFIX = '/api/browser-upload-sessions/';
const TOKEN_HEADER = 'x-imai-upload-session-token';

/**
 * Runs a session operation on the caller's own authenticated Convex client
 * (the `media/browser_uploads` actions) instead of the HTTP route, saving the
 * route hop and its token mint on every begin and complete.
 */
export type BrowserUploadDirectControl = (
  operation: 'begin' | 'complete' | 'abort',
  args: Record<string, unknown> & { sessionId: string; token: string }
) => Promise<unknown>;

export type BrowserUploadControl = {
  endpoint: string;
  token: string;
  direct?: BrowserUploadDirectControl;
};

/** The session control, routed through `direct` when the caller has one. */
export function withDirectBrowserUploadControl(
  control: BrowserUploadControl | null,
  direct: BrowserUploadDirectControl | undefined
): BrowserUploadControl | null {
  return control && direct ? { ...control, direct } : control;
}

export function parseBrowserUploadControl(
  uploadUrl: string
): BrowserUploadControl | null {
  if (!uploadUrl.startsWith(CONTROL_PREFIX)) return null;
  const hashIndex = uploadUrl.indexOf('#');
  if (hashIndex < 0 || uploadUrl.indexOf('?', 0) >= 0) return null;
  const endpoint = uploadUrl.slice(0, hashIndex);
  const token = uploadUrl.slice(hashIndex + 1);
  const sessionId = endpoint.slice(CONTROL_PREFIX.length);
  if (!/^[a-z0-9]+$/i.test(sessionId) || !token || token.includes('#'))
    return null;
  return { endpoint, token };
}

/** What `begin` answers. Single-PUT sessions add the signed PUT headers. */
export type BrowserUploadBegun = {
  uploadUrl: string;
  contentMd5: string;
  owner?: boolean;
  uploadHeaders?: Record<string, string>;
};

/**
 * `begin` fields every caller sends. `transport: 'put'` asks for one
 * presigned PutObject whose signed headers make R2 verify the SHA-256 at
 * write time, so `complete` only HEADs. Servers that predate it ignore it.
 */
export const BROWSER_UPLOAD_PUT_TRANSPORT = { transport: 'put' } as const;

/** The headers the session's one storage PUT must carry verbatim. */
export function browserUploadPutHeaders(
  begun: BrowserUploadBegun
): Record<string, string> {
  return begun.uploadHeaders ?? { 'Content-MD5': begun.contentMd5 };
}

/**
 * A single-PUT session answered 412: a replayed PUT found this session's
 * write already stored (write-once), so `complete` verifies that object.
 */
export function isStoredByEarlierPut(
  begun: Pick<BrowserUploadBegun, 'uploadHeaders'>,
  status: number
): boolean {
  return Boolean(begun.uploadHeaders) && status === 412;
}

/**
 * One id per upload call, sent with `begin` and `abort`. The server records
 * the attempt whose begin created the multipart upload and answers a replay
 * from any other attempt (a remount, a second caller) with `owner: false`;
 * only the owner may abort. `getRandomValues` works outside secure contexts.
 */
export function newBrowserUploadAttemptId(): string {
  return bytesToHex(crypto.getRandomValues(new Uint8Array(16)));
}

/**
 * Whether a `begin` answer lets this attempt abort the session. A server
 * that predates ownership omits `owner`; every begin it answers is fresh.
 */
export function ownsBrowserUploadSession(begun: { owner?: unknown }): boolean {
  return begun.owner !== false;
}

/**
 * A replayed `begin` that lands while the first one is still creating the
 * multipart upload gets 409 BROWSER_UPLOAD_BEGIN_IN_PROGRESS; the server
 * finishes the first and answers a later replay from it, so wait and retry.
 */
export const BROWSER_UPLOAD_BEGIN_RETRY_DELAYS_MS = [300, 600, 1200];

function waitForBeginRetry(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** POST one session control operation; retries `begin` while in progress. */
export async function fetchBrowserUploadControl(
  control: BrowserUploadControl,
  body: Record<string, unknown>,
  signal?: AbortSignal
): Promise<Response> {
  for (let attempt = 0; ; attempt += 1) {
    const response = await fetch(control.endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        [TOKEN_HEADER]: control.token,
      },
      body: JSON.stringify(body),
      signal,
    });
    const delay = BROWSER_UPLOAD_BEGIN_RETRY_DELAYS_MS[attempt];
    if (
      response.ok ||
      body.operation !== 'begin' ||
      response.status !== 409 ||
      delay === undefined
    )
      return response;
    const detail = await response
      .clone()
      .text()
      .catch(() => '');
    if (!detail.includes('BROWSER_UPLOAD_BEGIN_IN_PROGRESS')) return response;
    await waitForBeginRetry(delay, signal);
  }
}

function errorText(error: unknown): string {
  const data =
    error && typeof error === 'object' && 'data' in error ? error.data : '';
  return `${typeof data === 'string' ? data : ''} ${
    error instanceof Error ? error.message : String(error)
  }`;
}

/** One direct session operation; retries `begin` while in progress, like the route. */
async function callDirectBrowserUploadControl(
  control: BrowserUploadControl,
  direct: BrowserUploadDirectControl,
  body: Record<string, unknown>,
  signal: AbortSignal
): Promise<unknown> {
  const { operation, ...rest } = body;
  const args = {
    ...rest,
    sessionId: control.endpoint.slice(CONTROL_PREFIX.length),
    token: control.token,
  };
  for (let attempt = 0; ; attempt += 1) {
    signal.throwIfAborted();
    try {
      return await direct(operation as 'begin' | 'complete' | 'abort', args);
    } catch (error) {
      const delay = BROWSER_UPLOAD_BEGIN_RETRY_DELAYS_MS[attempt];
      if (
        operation !== 'begin' ||
        delay === undefined ||
        !errorText(error).includes('BROWSER_UPLOAD_BEGIN_IN_PROGRESS')
      )
        throw error;
      await waitForBeginRetry(delay, signal);
    }
  }
}

export async function postBrowserUploadControl<T>(
  control: BrowserUploadControl,
  body: Record<string, unknown>,
  signal: AbortSignal
): Promise<T> {
  if (
    control.direct &&
    (body.operation === 'begin' ||
      body.operation === 'complete' ||
      body.operation === 'abort')
  ) {
    const result = await callDirectBrowserUploadControl(
      control,
      control.direct,
      body,
      signal
    );
    if (
      typeof result === 'object' &&
      result !== null &&
      'refused' in result &&
      typeof result.refused === 'string'
    )
      throw new Error(`Upload session failed (409): ${result.refused}`);
    return result as T;
  }
  const response = await fetchBrowserUploadControl(control, body, signal);
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`Upload session failed (${response.status}): ${detail}`);
  }
  return (await response.json()) as T;
}

/** How long an abort sent after a cancel may take before it is dropped. */
const BROWSER_UPLOAD_CLEANUP_TIMEOUT_MS = 30_000;

/**
 * The signal for a session `abort` sent while cleaning up. Never the
 * upload's own signal: after a user cancel that one is already aborted, so
 * the abort would never leave the browser and the quota hold would stay.
 */
export function browserUploadCleanupSignal(): AbortSignal {
  return AbortSignal.timeout(BROWSER_UPLOAD_CLEANUP_TIMEOUT_MS);
}
